export enum AuthProvider {
    GOOGLE = 'GOOGLE',
    APPLE = 'APPLE',
    EMAIL = 'EMAIL',
    KAKAO = 'KAKAO',
    UNKNOWN = 'UNKNOWN'
}

export enum UserRole {
    SUPER_ADMIN = 'SUPER_ADMIN',
    ADMIN = 'ADMIN',
    USER = 'USER'
}

export enum UserLeague {
    BRONZE = 'BRONZE'
}

export enum SubscriptionType {
    FREE = 'FREE',
    MONTHLY = 'MONTHLY',
    YEARLY = 'YEARLY',
    LIFETIME = 'LIFETIME'
}

export enum UserDeletionMode {
    SOFT_DELETE = 'SOFT_DELETE',
    HARD_DELETE = 'HARD_DELETE',
}

export enum DeviceType {
    ANDROID = 'ANDROID',
    IOS = 'IOS',
    WEB = 'WEB',
}

export interface User {
    id: number;
    firebaseUid: string;
    email: string;
    nickname?: string | null;
    provider: AuthProvider;
    role: UserRole;
    nativeLanguageCode: string;
    totalXp: number;
    totalPoint: number;
    league: UserLeague;
    subscriptionType: SubscriptionType;
    isSubscriptionActive: boolean;
    subscriptionExpiresAt?: string | null;
    lastLoginAt: string;
    deviceType?: DeviceType | null;
    countryCode?: string | null;
    timezone?: string | null;
    aiCreditBalance?: AiCreditBalance | null;
    deletedAt?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface UserWordbookInfo {
    wordbookId: number;
    wordbookTitle: string;
    libraryId?: number | null;
    groupId?: number | null;
    groupTitle?: string | null;
    isGroupEntry?: boolean;
    learnedWords: number;
    totalWords: number;
    completedAt: string | null;
    createdAt: string;
}

export interface DeletionHistoryInfo {
    originalEmail: string;
    originalNickname?: string | null;
    originalFirebaseUid: string;
    provider: AuthProvider;
    deletionType: string;
    deletedBy?: string | null;
    deletedAt: string;
}

export interface UserWordbookSummary {
    totalWordbooks: number;
    completedWordbooks: number;
    inProgressWordbooks: number;
    totalGroups: number;
    totalWords: number;
}

export interface UserLibraryItemInfo {
    libraryId: number;
    title: string;
    itemType: string;
    wordbookId?: number | null;
    groupId?: number | null;
    totalWords: number;
    libraryType: string;
    isVisible: boolean;
    acquiredAt: string;
}

export interface UserLibrarySummary {
    ownedWordbookLibraries: number;
    ownedGroupLibraries: number;
    totalOwnedLibraries: number;
    recentLibraries?: UserLibraryItemInfo[];
}

export interface UserStudySummary {
    totalSessions: number;
    sessionsLast7Days: number;
    sessionsLast30Days: number;
    totalItems: number;
    totalStudyMinutes: number;
    totalCorrectAnswers: number;
    totalWrongAnswers: number;
    totalCorrectAttempts: number;
    totalWrongAttempts: number;
    accuracyRate: number;
    lastStudiedAt?: string | null;
}

export interface AiCreditCosts {
    [key: string]: number;
}

export interface AiCreditBalance {
    balance: number;
    grantedAmount: number;
    periodStart: string;
    nextResetAt: string;
    isSubscribed: boolean;
    costs: AiCreditCosts;
}

export interface EppiSummary {
    hatched: boolean;
    ownedCostumeCount: number;
    roomItemCount: number;
    roomDecorated: boolean;
    representativeCostumeName?: string | null;
}

export interface UserDetail extends User {
    birthYear?: number | null;
    nativeLanguageName?: string | null;
    totalPointsPurchased?: number;
    subscriptionStartedAt?: string | null;
    isAutoRenew?: boolean | null;
    aiCreditBalance?: AiCreditBalance | null;
    wordbookStats?: UserWordbookSummary;
    libraryStats?: UserLibrarySummary;
    studyStats?: UserStudySummary;
    recentWordbooks?: UserWordbookInfo[];
    deletionHistory?: DeletionHistoryInfo | null;
    // 활동 요약 (2026-07-06 활동 중심 개편)
    masteredWordCount: number;
    missionClaimCount: number;
    lastMissionClaimedAt?: string | null;
    xpBalance: number;
    totalSpentXp: number;
    eppiSummary?: EppiSummary | null;
}

// ========== 활동 탭 (2026-07-06 활동 중심 개편) ==========

export interface MasteredWordItem {
    dictId: number;
    word: string;
    meaning?: string | null;
    headLang: string;
    meaningLang: string;
    masteredAt?: string | null;
    masteryScore?: number | null;
}

export interface MissionClaimItem {
    id: number;
    missionCode: string;
    missionName: string;
    tierIndex: number;
    rewardXp: number;
    rewardCostumeName?: string | null;
    periodKey: string;
    claimedAt: string;
}

export interface EppiEquippedSlotItem {
    slotType: string;
    costumeName: string;
    equippedAt: string;
}

export interface EppiOwnedCostumeItem {
    costumeId: number;
    name: string;
    categoryName?: string | null;
    renderKind?: string | null;
    isMissionReward: boolean;
    acquiredAt: string;
    purchaseXpCost: number;
}

export interface EppiRoomItem {
    slotIndex: number;
    costumeName: string;
    text?: string | null;
}

export interface EppiDetail {
    hatched: boolean;
    representativeCostumeName?: string | null;
    equippedSlots: EppiEquippedSlotItem[];
    ownedCostumes: EppiOwnedCostumeItem[];
    roomItems: EppiRoomItem[];
}

export interface FeatherHistoryItem {
    id: number;
    delta: number;
    reason: string;
    relatedName?: string | null;
    createdAt: string;
}

export interface AiCreditHistoryItem {
    id: number;
    delta: number;
    reason: string;
    balanceAfter: number;
    relatedEntity?: string | null;
    metadata?: string | null;
    createdAt: string;
}

export interface CreditUsageByReason {
    reason: string;
    totalSpent: number;
}

export interface AdminAiCreditDetail {
    balance: AiCreditBalance | null;
    history: AiCreditHistoryItem[];
    usageByReason: CreditUsageByReason[];
}

export interface UserReportItem {
    id: number;
    domainWordId: number;
    wordText?: string | null;
    meaningText?: string | null;
    reportType: string;
    reportTypeDescription: string;
    quizType: string;
    reportText: string;
    status: string;
    statusDescription: string;
    adminNote?: string | null;
    createdAt: string;
}

export interface UserInquiryItem {
    id: number;
    category: string;
    categoryDescription: string;
    title: string;
    status: string;
    statusDescription: string;
    hasAnswer: boolean;
    isRead: boolean;
    createdAt: string;
    answeredAt?: string | null;
}

export interface UserPushHistoryItem {
    id: number;
    targetType: string;
    title: string;
    body: string;
    imageUrl?: string | null;
    totalTargetCount: number;
    successCount: number;
    failureCount: number;
    sentAt: string;
}

export enum PurchaseStatus {
    COMPLETED = 'COMPLETED',
    REFUNDED = 'REFUNDED',
}

export enum SubscriptionStatus {
    ACTIVE = 'ACTIVE',
    EXPIRED = 'EXPIRED',
    CANCELLED = 'CANCELLED',
    REFUNDED = 'REFUNDED',
}

export interface RewardHistoryItem {
    id: number;
    pk: string;
    xp: number;
    point: number;
    comment: string;
    createdAt: string;
}

export interface PointPurchaseItem {
    id: number;
    platform: string;
    productId: string;
    points: number;
    bonusPoints: number;
    totalPoints: number;
    price: number;
    currency: string;
    status: PurchaseStatus;
    purchaseDate: string;
    refundedDate?: string | null;
    createdAt: string;
}

export interface SubscriptionHistoryItem {
    id: number;
    platform: string;
    productId: string;
    subscriptionType: SubscriptionType;
    status: SubscriptionStatus;
    price: number;
    currency: string;
    purchaseDate: string;
    expiresDate: string;
    cancelledDate?: string | null;
    isTrial: boolean;
    isAutoRenew: boolean;
    createdAt: string;
}
